import React from 'react';
import {Box, ButtonBase, Collapse, Typography} from '@mui/material';
import {ExpandMore, ExpandLess} from '@mui/icons-material';
import {useExpandableSection} from './useExpandableSection';

interface ExpandableSectionProps {
  /** Content hidden behind the expand/collapse toggle. */
  children: React.ReactNode;
  /** Controlled expanded state. When `undefined`, internal state is used. */
  expanded?: boolean;
  /** Callback fired when the toggle is clicked. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Initial expanded state for uncontrolled mode. Defaults to `false`. */
  defaultExpanded?: boolean;
  /** MUI Collapse `timeout` value. Defaults to `"auto"`. */
  timeout?: number | 'auto';
  /** Accessible label when the section is collapsed. Defaults to `"Show more"`. */
  expandLabel?: string;
  /** Accessible label when the section is expanded. Defaults to `"Show less"`. */
  collapseLabel?: string;
}

/**
 * Generic expand/collapse section.
 *
 * Renders an expand/collapse toggle and wraps `children` in a MUI `Collapse`.
 * Supports both controlled (`expanded` + `onExpandedChange`) and uncontrolled modes.
 *
 * @example
 * <ExpandableSection>
 *   <ExtraFilters />
 * </ExpandableSection>
 *
 * @example
 * // Controlled via URL param
 * const [open, setOpen] = useBindSearchParam<'true' | 'false'>('open', 'false');
 * <ExpandableSection expanded={open === 'true'} onExpandedChange={(v) => setOpen(v ? 'true' : 'false')}>
 *   <ExtraFilters />
 * </ExpandableSection>
 */
const ExpandableSection = ({
  children,
  expanded,
  onExpandedChange,
  defaultExpanded,
  timeout = 'auto',
  expandLabel = 'Show more',
  collapseLabel = 'Show less',
}: ExpandableSectionProps) => {
  const {isExpanded, handleExpandToggle} = useExpandableSection({
    expanded,
    onExpandedChange,
    defaultExpanded,
  });
  const toggleId = React.useId();
  const contentId = React.useId();

  return (
    <>
      <ButtonBase
        id={toggleId}
        onClick={handleExpandToggle}
        role={'button'}
        aria-label={isExpanded ? collapseLabel : expandLabel}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: 'flex-start',
          borderRadius: 1,
          py: 0.5,
          pr: 1,
          transition: theme.transitions.create(['background-color', 'box-shadow'], {
            duration: theme.transitions.duration.shortest,
          }),
          '&.Mui-focusVisible, &:focus-visible': {
            backgroundColor: theme.palette.action.hover,
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          },
        })}
      >
        <Box
          sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, minHeight: 32}}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </Box>
        <Typography component="span" variant="body2" color="textSecondary">
          {isExpanded ? collapseLabel : expandLabel}
        </Typography>
      </ButtonBase>
      <Collapse in={isExpanded} timeout={timeout}>
        <Box id={contentId} aria-labelledby={toggleId}>{children}</Box>
      </Collapse>
    </>
  );
};

export default ExpandableSection;

export type {ExpandableSectionProps};

